// src/components/layout/Breadcrumbs.jsx
import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Breadcrumbs as MUIBreadcrumbs, Link, Typography } from "@mui/material";
import { useBreadcrumb } from "../../context/BreadcrumbContext";

export default function Breadcrumbs() {
  const location = useLocation();
  const { getLabel, customPath } = useBreadcrumb();
  const pathnames = location.pathname.split("/").filter((x) => x);

  const nameMap = {
    dashboard: "Dashboard",
    properties: "Properties",
    units: "Units",
    insurance: "Insurance",
    utilities: "Utilities",
    assessments: "Assessments",
    create: "Create",
    edit: "Edit",
    admin: "Admin",
    users: "Users",
    assignments: "Assignments",
    tickets: "Tickets",
    suppliers: "Suppliers",
    renewals: "Renewals",
    settings: "Settings",
    lpe1: "LPE1",
  };

  // If a custom path is set, use it instead of auto-generating
  if (customPath && customPath.length > 0) {
    return (
      <MUIBreadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "0.95rem" }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="primary">
          Home
        </Link>
        {customPath.map((item, idx) => {
          const isLast = idx === customPath.length - 1;
          return isLast || !item.path ? (
            <Typography key={idx} color="text.primary">
              {item.label}
            </Typography>
          ) : (
            <Link
              key={idx}
              component={RouterLink}
              to={item.path}
              underline="hover"
              color="primary"
            >
              {item.label}
            </Link>
          );
        })}
      </MUIBreadcrumbs>
    );
  }

  // Build breadcrumb items, using custom labels for IDs when available
  const breadcrumbItems = [];
  
  for (let index = 0; index < pathnames.length; index++) {
    const value = pathnames[index];
    const routeTo = "/" + pathnames.slice(0, index + 1).join("/");
    const isLast = index === pathnames.length - 1;
    
    // Check if this is an ID (numeric) and we have a custom label for it
    const isId = !isNaN(value);
    const customLabel = getLabel(value);
    
    // Skip "details" segment if we have a custom label for the ID before it
    if (value.toLowerCase() === "details") {
      const prevValue = pathnames[index - 1];
      if (prevValue && getLabel(prevValue)) {
        continue; // Skip "details" - we already show the name
      }
    }
    
    // Skip IDs that don't have custom labels
    if (isId && !customLabel) {
      continue;
    }
    
    // Determine the label to display
    let label;
    if (customLabel) {
      label = customLabel;
    } else {
      label = nameMap[value.toLowerCase()] ||
        value.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    }
    
    breadcrumbItems.push({
      label,
      routeTo,
      isLast: isLast || (index === pathnames.length - 2 && pathnames[index + 1]?.toLowerCase() === "details"),
    });
  }

  return (
    <MUIBreadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "0.95rem" }}>
      {/* Home always appears */}
      <Link component={RouterLink} to="/dashboard" underline="hover" color="primary">
        Home
      </Link>

      {breadcrumbItems.map((item, idx) => {
        const isActuallyLast = idx === breadcrumbItems.length - 1;
        
        return isActuallyLast ? (
          <Typography key={item.routeTo} color="text.primary">
            {item.label}
          </Typography>
        ) : (
          <Link
            key={item.routeTo}
            component={RouterLink}
            to={item.routeTo}
            underline="hover"
            color="primary"
          >
            {item.label}
          </Link>
        );
      })}
    </MUIBreadcrumbs>
  );
}
